import { useEffect, useState } from "react";
import { Check, Monitor, Moon, Sun } from "lucide-react";

import { languageRegionMessage } from "../i18n/languageRegionCatalog";
import { useWorkspaceCopy } from "../i18n/workspaceCatalog";
import { useDismissibleLayer } from "./useDismissibleLayer";
import {
  readSharedThemePreference,
  setSharedThemePreference,
  subscribeSharedTheme,
  type ThemePreference,
} from "./theme";
import "./theme-preference-control.css";

export type ThemePreferenceLabels = {
  appearance: string;
  system: string;
  light: string;
  dark: string;
};

const OPTIONS: ReadonlyArray<{
  value: ThemePreference;
  icon: typeof Monitor;
  label: keyof Omit<ThemePreferenceLabels, "appearance">;
}> = [
  { value: "system", icon: Monitor, label: "system" },
  { value: "light", icon: Sun, label: "light" },
  { value: "dark", icon: Moon, label: "dark" },
];

export function ThemePreferenceControl({
  labels: suppliedLabels,
  variant = "menu",
}: {
  labels?: ThemePreferenceLabels;
  variant?: "menu" | "segmented";
}) {
  const { locale } = useWorkspaceCopy();
  const labels = suppliedLabels ?? {
    appearance: languageRegionMessage(locale, "themeTitle"),
    system: languageRegionMessage(locale, "themeSystem"),
    light: languageRegionMessage(locale, "themeLight"),
    dark: languageRegionMessage(locale, "themeDark"),
  };
  const [preference, setPreference] = useState<ThemePreference>(
    () => readSharedThemePreference(),
  );
  const [open, setOpen] = useState(false);
  const layerRef = useDismissibleLayer<HTMLDivElement>(open, () => setOpen(false));
  const ActiveIcon = OPTIONS.find((item) => item.value === preference)?.icon ?? Monitor;

  useEffect(() => subscribeSharedTheme(setPreference), []);

  function select(next: ThemePreference) {
    setSharedThemePreference(next);
    setPreference(next);
    setOpen(false);
  }

  if (variant === "segmented") return <div className="kavoosh-theme-segments" role="group" aria-label={labels.appearance}>
    {(["light", "dark", "system"] as const).map(value => {
      const item = OPTIONS.find(option => option.value === value)!;
      const Icon = item.icon;
      return <button key={value} type="button" aria-pressed={preference === value} onClick={() => select(value)}><Icon size={17} aria-hidden="true" />{labels[item.label]}</button>;
    })}
  </div>;

  return <div ref={layerRef} className="kavoosh-preference-control">
    <button
      type="button"
      className="kavoosh-preference-trigger"
      aria-label={labels.appearance}
      title={labels.appearance}
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={() => setOpen((value) => !value)}
    >
      <ActiveIcon size={18} aria-hidden="true" />
    </button>
    {open ? <div className="kavoosh-preference-menu" role="menu" aria-label={labels.appearance}>
      {OPTIONS.map((item) => {
        const Icon = item.icon;
        return <button
          key={item.value}
          type="button"
          role="menuitemradio"
          aria-checked={preference === item.value}
          onClick={() => select(item.value)}
        >
          <Icon size={17} aria-hidden="true" />
          <span>{labels[item.label]}</span>
          {preference === item.value ? <Check size={15} aria-hidden="true" /> : null}
        </button>;
      })}
    </div> : null}
  </div>;
}
